import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useImageProxyUrls } from "@/hooks/useImageProxyUrl";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import {
  GalleryAddTile,
  GalleryImageTile,
  GalleryPlaceholderTile,
  ImageGalleryGrid,
} from "@/components/ui/image-gallery-grid";

export interface PhotoEvidenceGridProps {
  /** Array of storage paths or URLs. */
  images: string[];
  /** Maximum number of grid columns on large screens. Mock default is 4 (screen-07). */
  columns?: 2 | 3 | 4;
  /** Open the lightbox on tile click (default: true). */
  clickable?: boolean;
  /** When set, an "Add Photo" tile is appended and clicking it calls this. */
  onAdd?: () => void;
  /** When set, each tile shows a delete control; called with the image index. */
  onRemove?: (index: number) => void;
  /** Convenience alias: render the add tile (requires onAdd to do anything). */
  editable?: boolean;
  className?: string;
}

interface TileGridProps {
  proxyUrls: (string | null)[];
  columns: 2 | 3 | 4;
  clickable: boolean;
  showAddTile: boolean;
  onView: (proxyUrl: string) => void;
  onRemove?: (index: number) => void;
  onAdd?: () => void;
  className?: string;
}

function TileGrid({
  proxyUrls,
  columns,
  clickable,
  showAddTile,
  onView,
  onRemove,
  onAdd,
  className,
}: TileGridProps) {
  const { t } = useTranslation();
  const viewLabel = t("defects.viewImage", { defaultValue: "Bild ansehen" });
  const removeLabel = t("common.delete", { defaultValue: "Löschen" });
  const addLabel = t("defects.addPhoto", { defaultValue: "Foto hinzufügen" });

  const renderTile = (proxyUrl: string | null, idx: number) => {
    if (!proxyUrl)
      return <GalleryPlaceholderTile key={idx} data-testid={`photo-evidence-placeholder-${idx}`} />;
    return (
      <GalleryImageTile
        key={idx}
        data-testid={`photo-evidence-tile-${idx}`}
        image={
          <AuthenticatedImage
            src={proxyUrl}
            alt={`${t("defects.image", { count: 1 })} ${idx + 1}`}
            className="h-full w-full object-cover"
          />
        }
        onView={clickable ? () => onView(proxyUrl) : undefined}
        onRemove={onRemove ? () => onRemove(idx) : undefined}
        viewLabel={viewLabel}
        removeLabel={removeLabel}
        viewTestId={`photo-evidence-view-${idx}`}
        removeTestId={`photo-evidence-remove-${idx}`}
      />
    );
  };

  return (
    <ImageGalleryGrid data-testid="photo-evidence-grid" columns={columns} className={className}>
      {proxyUrls.map(renderTile)}
      {showAddTile && (
        <GalleryAddTile
          data-testid="photo-evidence-add"
          label={addLabel}
          onClick={onAdd}
          disabled={!onAdd}
        />
      )}
    </ImageGalleryGrid>
  );
}

/**
 * Industrial photo evidence grid (Stitch "Industrial Management Portal", screen-07 L288-303).
 *
 * Consolidated successor of `DefectImageGallery` — it reuses the SAME image path
 * (`useImageProxyUrls` → `AuthenticatedImage` → `ImageLightbox`), so there is no second
 * image pipeline. The tile/grid shells live in @plasser/ui (`ImageGalleryGrid` family,
 * TES-1999): large adaptive `aspect-square` tiles with the hover overlay (`bg-primary/40`
 * + visibility icon) and an optional embedded "Add Photo" tile (only when `onAdd`/
 * `editable` is set). Columns scale with the tile count so few images render LARGE
 * (TES-2000) — `columns` is the cap on big screens.
 */
export function PhotoEvidenceGrid({
  images,
  columns = 4,
  clickable = true,
  onAdd,
  onRemove,
  editable,
  className,
}: PhotoEvidenceGridProps) {
  const proxyUrls = useImageProxyUrls(images);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const validUrls = proxyUrls.filter((url): url is string => !!url);
  const showAddTile = Boolean(editable) || Boolean(onAdd);

  if (proxyUrls.length === 0 && !showAddTile) return null;

  const openLightbox = (proxyUrl: string) =>
    setLightbox({ open: true, index: validUrls.indexOf(proxyUrl) });

  return (
    <>
      <TileGrid
        proxyUrls={proxyUrls}
        columns={columns}
        clickable={clickable}
        showAddTile={showAddTile}
        onView={openLightbox}
        onRemove={onRemove}
        onAdd={onAdd}
        className={className}
      />
      <EvidenceLightbox
        images={validUrls}
        enabled={clickable}
        state={lightbox}
        onOpenChange={(open) => setLightbox((s) => ({ ...s, open }))}
      />
    </>
  );
}

function EvidenceLightbox({
  images,
  enabled,
  state,
  onOpenChange,
}: {
  images: string[];
  enabled: boolean;
  state: { open: boolean; index: number };
  onOpenChange: (open: boolean) => void;
}) {
  if (!enabled || images.length === 0) return null;
  return (
    <ImageLightbox
      images={images}
      initialIndex={state.index}
      open={state.open}
      onOpenChange={onOpenChange}
    />
  );
}
